import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL, counterpartyApi } from '../services/api';
import './TransactionPage.css';
import './CouponMaturityBlotter.css';

const CouponMaturityBlotter = () => {
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [counterparty, setCounterparty] = useState('');
  const [data, setData] = useState([]);
  const [totals, setTotals] = useState({ buy: { count: 0, total_amount: 0 }, sell: { count: 0, total_amount: 0 } });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Counterparty dropdown state
  const [counterpartyOptions, setCounterpartyOptions] = useState([]);
  const [counterpartyLoading, setCounterpartyLoading] = useState(true);

  useEffect(() => {
    async function fetchCounterparties() {
      setCounterpartyLoading(true);
      try {
        const data = await counterpartyApi.getAll();
        setCounterpartyOptions([
          { value: '', label: 'All Counterparties' },
          ...(data || []).map(cp => ({
            value: cp.unique_id,
            label: `${cp.short_name} (${cp.unique_id})`
          }))
        ]);
      } catch (e) {
        console.error('Error fetching counterparties:', e);
        setCounterpartyOptions([{ value: '', label: 'All Counterparties' }]);
      } finally {
        setCounterpartyLoading(false);
      }
    }
    fetchCounterparties();
  }, []);

  // Remove auto-fetch on date change - user will click Refresh button

  const fetchCouponData = async () => {
    if (!startDate || !endDate) {
      setError('Please select both start date and end date');
      return;
    }
    
    if (new Date(startDate) > new Date(endDate)) {
      setError('Start date cannot be after end date');
      return;
    }
    
    setLoading(true);
    setError('');
    try {
      const params = { 
        startDate,
        endDate
      };
      if (counterparty) {
        params.counterparty = counterparty;
      }
      const response = await axios.get(`${API_BASE_URL}/coupon-maturity-blotter`, {
        params
      });
      
      if (response.data.success) {
        setData(response.data.data || []);
        setTotals(response.data.totals || { buy: { count: 0, total_amount: 0 }, sell: { count: 0, total_amount: 0 } });
      } else {
        setError(response.data.error || 'Failed to fetch coupon data');
        setData([]);
      }
    } catch (err) {
      console.error('Error fetching coupon maturity blotter:', err);
      setError(err.response?.data?.error || 'Failed to fetch coupon data');
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '0.00';
    return parseFloat(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB');
    } catch {
      return dateStr;
    }
  };

  // Separate Buy and Sell transactions
  const buyTransactions = data.filter(d => d.transaction_type === 'Buy');
  const sellTransactions = data.filter(d => d.transaction_type === 'Sell');

  return (
    <div className="page-container coupon-blotter-container" style={{ padding: '24px' }}>
      <h2 style={{ marginBottom: '24px', color: '#000' }}>Coupon Maturity Blotter</h2>
      
      {/* Filters */}
      <div style={{ marginBottom: '24px', display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
        <div>
          <label style={{ fontWeight: 'bold', fontSize: '1rem', color: '#000', display: 'block', marginBottom: '4px' }}>Start Date:</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '1rem'
            }}
          />
        </div>
        <div>
          <label style={{ fontWeight: 'bold', fontSize: '1rem', color: '#000', display: 'block', marginBottom: '4px' }}>End Date:</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '1rem'
            }}
          />
        </div>
        <div>
          <label style={{ fontWeight: 'bold', fontSize: '1rem', color: '#000', display: 'block', marginBottom: '4px' }}>Counterparty:</label>
          <select
            value={counterparty}
            onChange={(e) => setCounterparty(e.target.value)}
            disabled={counterpartyLoading}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '1rem',
              minWidth: '200px'
            }}
          >
            {counterpartyLoading ? (
              <option value="">Loading...</option>
            ) : (
              counterpartyOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))
            )}
          </select>
        </div>
        <div style={{ alignSelf: 'flex-end' }}>
          <button
            onClick={fetchCouponData}
            disabled={loading}
            style={{
              padding: '8px 16px',
              background: '#007bff',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? 'Loading...' : 'Generate Report'}
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{ 
          padding: '12px', 
          background: '#fee', 
          color: '#c33', 
          borderRadius: '4px', 
          marginBottom: '16px' 
        }}>
          {error}
        </div>
      )}

      {/* Summary Totals */}
      {!loading && data.length > 0 && (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gap: '16px', 
          marginBottom: '24px' 
        }}>
          <div style={{ 
            padding: '16px', 
            background: '#e8f5e9', 
            borderRadius: '8px', 
            border: '2px solid #4caf50' 
          }}>
            <h3 style={{ margin: '0 0 8px 0', color: '#000' }}>Our Coupons (Buy)</h3>
            <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#000' }}>
              Count: {totals.buy.count} | Total: {formatCurrency(totals.buy.total_amount)}
            </div>
          </div>
          <div style={{ 
            padding: '16px', 
            background: '#ffebee', 
            borderRadius: '8px', 
            border: '2px solid #f44336' 
          }}>
            <h3 style={{ margin: '0 0 8px 0', color: '#000' }}>Counterparty Coupons (Sell)</h3>
            <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#000' }}>
              Count: {totals.sell.count} | Total: {formatCurrency(totals.sell.total_amount)}
            </div>
          </div>
        </div>
      )}

      {/* Loading Indicator */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#000' }}>
          <div>Loading coupon data...</div>
        </div>
      )}

      {/* Data Tables */}
      {!loading && data.length > 0 && (
        <div className="coupon-tables-grid">
          {/* Buy Transactions (Green) */}
          <div className="coupon-table-wrapper">
            <h3 style={{ 
              marginBottom: '16px', 
              color: '#000',
              padding: '8px',
              background: '#e8f5e9',
              borderRadius: '4px',
              width: '100%',
              boxSizing: 'border-box'
            }}>
              Our Coupons (Buy) - {buyTransactions.length} deals
            </h3>
            <div className="coupon-table-container">
              <table className="coupon-table">
                <thead>
                  <tr style={{ background: '#4caf50', color: '#000' }}>
                    <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left', color: '#000' }}>Deal Number</th>
                    <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left', color: '#000' }}>ISIN</th>
                    <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left', color: '#000' }}>Counterparty</th>
                    <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center', color: '#000' }}>Coupon Date</th>
                    <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right', color: '#000' }}>Face Value</th>
                    <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right', color: '#000' }}>Coupon Amount</th>
                    <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center', color: '#000' }}>Final</th>
                  </tr>
                </thead>
                <tbody>
                  {buyTransactions.map((deal) => (
                    <tr 
                      key={deal.id} 
                      style={{ 
                        background: '#f1f8e9',
                        borderLeft: '4px solid #4caf50',
                        color: '#000'
                      }}
                    >
                      <td style={{ padding: '8px', border: '1px solid #ddd', color: '#000' }}>{deal.deal_number}</td>
                      <td style={{ padding: '8px', border: '1px solid #ddd', fontFamily: 'monospace', color: '#000' }}>{deal.isin}</td>
                      <td style={{ padding: '8px', border: '1px solid #ddd', color: '#000' }}>{deal.counterparty_name}</td>
                      <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center', color: '#000' }}>
                        {deal.coupon_date ? formatDate(deal.coupon_date) : '-'}
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right', color: '#000' }}>
                        {formatCurrency(deal.display_face_value ?? deal.remaining_face_value ?? deal.face_value)}
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right', fontWeight: 'bold', color: '#000' }}>
                        {formatCurrency(deal.coupon_amount)}
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center', color: '#000' }}>
                        {deal.is_final_coupon ? '✓' : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Sell Transactions (Red) */}
          <div className="coupon-table-wrapper">
            <h3 style={{ 
              marginBottom: '16px', 
              color: '#000',
              padding: '8px',
              background: '#ffebee',
              borderRadius: '4px',
              width: '100%',
              boxSizing: 'border-box'
            }}>
              Counterparty Coupons (Sell) - {sellTransactions.length} deals
            </h3>
            <div className="coupon-table-container">
              <table className="coupon-table">
                <thead>
                  <tr style={{ background: '#f44336', color: '#000' }}>
                    <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left', color: '#000' }}>Deal Number</th>
                    <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left', color: '#000' }}>ISIN</th>
                    <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left', color: '#000' }}>Counterparty</th>
                    <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center', color: '#000' }}>Coupon Date</th>
                    <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right', color: '#000' }}>Face Value</th>
                    <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right', color: '#000' }}>Coupon Amount</th>
                    <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center', color: '#000' }}>Final</th>
                  </tr>
                </thead>
                <tbody>
                  {sellTransactions.map((deal) => (
                    <tr 
                      key={deal.id} 
                      style={{ 
                        background: '#ffebee',
                        borderLeft: '4px solid #f44336',
                        color: '#000'
                      }}
                    >
                      <td style={{ padding: '8px', border: '1px solid #ddd', color: '#000' }}>{deal.deal_number}</td>
                      <td style={{ padding: '8px', border: '1px solid #ddd', fontFamily: 'monospace', color: '#000' }}>{deal.isin}</td>
                      <td style={{ padding: '8px', border: '1px solid #ddd', color: '#000' }}>{deal.counterparty_name}</td>
                      <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center', color: '#000' }}>
                        {deal.coupon_date ? formatDate(deal.coupon_date) : '-'}
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right', color: '#000' }}>
                        {formatCurrency(deal.display_face_value ?? deal.remaining_face_value ?? deal.face_value)}
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right', fontWeight: 'bold', color: '#000' }}>
                        {formatCurrency(deal.coupon_amount)}
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center', color: '#000' }}>
                        {deal.is_final_coupon ? '✓' : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* No Data Message */}
      {!loading && data.length === 0 && !error && (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          color: '#000',
          background: '#f5f5f5',
          borderRadius: '8px'
        }}>
          <p>No coupon payments found for the selected period: {formatDate(startDate)} to {formatDate(endDate)}</p>
        </div>
      )}
    </div>
  );
};

export default CouponMaturityBlotter;

